/**
 * Shopify Search & Discovery Facets
 * Handles AJAX filtering for collection pages
 */

class FacetFiltersForm {
  constructor() {
    this.filterData = [];
    this.searchParamsInitial = window.location.search.slice(1);
    this.searchParamsPrev = window.location.search.slice(1);
    this.keepMobileDrawerOpen = false;
    this.isFirstLoad = true;
    this.pendingFilterState = null;
    this.eventListenersSetup = false;
    this.init();
  }

  init() {
    // Set up event listeners
    this.setupEventListeners();

    // Initialize mobile drawer state
    this.initializeMobileDrawerState();

    // Initialize range sliders
    this.initializeRangeSliders();

    // Preload common filter combinations for better performance
    this.preloadCommonFilters();

    // Handle browser back/forward
    window.addEventListener("popstate", (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : this.searchParamsInitial;
      if (searchParams === this.searchParamsPrev) return;
      this.renderPage(searchParams, null, false);
    });
  }

  setupEventListeners() {
    // Prevent duplicate event listeners by checking if they're already set up
    if (this.eventListenersSetup) {
      return;
    }

    // Handle filter checkboxes
    this.filterChangeHandler = (event) => {
      if (event.target.matches(".facets__checkbox")) {
        this.handleFilterChange(event);
      }
    };
    document.addEventListener("change", this.filterChangeHandler);

    // Handle price range submissions
    this.priceSubmitHandler = (event) => {
      if (event.target.matches(".facets__price-submit")) {
        this.handlePriceSubmit(event);
      }
    };
    document.addEventListener("click", this.priceSubmitHandler);

    // Handle range slider changes
    this.rangeSliderHandler = (event) => {
      if (event.target.matches(".price-range-slider__input")) {
        this.handleRangeSliderChange(event);
      }
    };
    document.addEventListener("input", this.rangeSliderHandler);
    document.addEventListener("change", this.rangeSliderHandler);

    // Handle mobile touch events specifically
    this.mobileRangeHandler = (event) => {
      if (event.target.matches(".price-range-slider__handle")) {
        return;
      }

      if (event.target.matches(".price-range-slider__input")) {
        if (event.type === "touchmove") {
          if (event.cancelable) {
            event.preventDefault();
          }
        }

        this.handleRangeSliderChange(event);
      }
    };
    document.addEventListener("touchstart", this.mobileRangeHandler);
    document.addEventListener("touchmove", this.mobileRangeHandler);
    document.addEventListener("touchend", this.mobileRangeHandler);

    // Handle custom range slider handles
    this.rangeHandleHandler = (event) => {
      if (event.target.matches(".price-range-slider__handle")) {
        this.handleRangeHandleDrag(event);
      }
    };
    document.addEventListener("mousedown", this.rangeHandleHandler);
    document.addEventListener("touchstart", this.rangeHandleHandler);

    // Handle sort changes
    this.sortChangeHandler = (event) => {
      if (event.target.matches("#SortBy")) {
        this.handleSortChange(event);
      }
    };
    document.addEventListener("change", this.sortChangeHandler);

    // Handle remove filter buttons
    this.removeFilterHandler = (event) => {
      if (
        event.target.closest(".selected-filter-tag__remove") ||
        event.target.closest(".selected-filters__clear-all")
      ) {
        this.handleRemoveFilter(event);
      }
    };
    document.addEventListener("click", this.removeFilterHandler);

    // Listen for drawer state changes using MutationObserver only
    // This approach doesn't interfere with the theme's native drawer functionality
    this.setupDrawerObserver();

    this.eventListenersSetup = true;
  }

  handleFilterChange(event) {
    const form = document.querySelector("#FacetFiltersForm");
    if (!form) {
      return;
    }

    // Check if this is from mobile drawer and keep it open
    const isFromMobileDrawer = event.target.closest("#FilterDrawer");
    if (isFromMobileDrawer) {
      // Store that we want to keep the drawer open
      this.keepMobileDrawerOpen = true;

      // Store the current filter state before updating
      this.storeCurrentFilterState(event.target);
    }

    // Get current URL parameters
    const currentUrl = new URL(window.location);
    const currentParams = new URLSearchParams(currentUrl.search);

    // Get the checkbox that was changed
    const checkbox = event.target;
    const paramName = checkbox.name;
    const paramValue = checkbox.value;

    if (checkbox.checked) {
      // Add the filter parameter (append for multiple values)
      currentParams.append(paramName, paramValue);
    } else {
      // Remove the specific filter parameter value
      const allValues = currentParams.getAll(paramName);
      const filteredValues = allValues.filter((value) => value !== paramValue);

      // Remove all instances of this parameter
      currentParams.delete(paramName);

      // Add back the remaining values
      filteredValues.forEach((value) => {
        currentParams.append(paramName, value);
      });
    }

    const searchParams = currentParams.toString();

    this.renderPage(searchParams, event);
  }

  handlePriceSubmit(event) {
    event.preventDefault();

    // Prevent duplicate submissions
    if (this.isSubmittingPrice) {
      return;
    }
    this.isSubmittingPrice = true;

    const form = document.querySelector("#FacetFiltersForm");
    if (!form) {
      this.isSubmittingPrice = false;
      return;
    }

    // Ensure range slider values are properly set in form inputs
    this.updateFormInputsWithRangeValues();

    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();

    this.renderPage(searchParams, event);

    // Reset the flag immediately after renderPage starts
    this.isSubmittingPrice = false;
  }

  storeCurrentRangeValues() {
    const rangeSliders = document.querySelectorAll(".price-range-slider");
    rangeSliders.forEach((container) => {
      const minSlider = container.querySelector(
        ".price-range-slider__input--min"
      );
      const maxSlider = container.querySelector(
        ".price-range-slider__input--max"
      );

      if (minSlider && maxSlider) {
        this.currentRangeValues = {
          min: parseInt(minSlider.value),
          max: parseInt(maxSlider.value),
          minSlider: minSlider,
          maxSlider: maxSlider,
        };
      }
    });
  }

  updateFormInputsWithRangeValues() {
    if (!this.currentRangeValues) return;

    const form = document.querySelector("#FacetFiltersForm");
    if (!form) return;

    const minInput = form.querySelector(
      `input[name="${this.currentRangeValues.minSlider.name}"]`
    );
    const maxInput = form.querySelector(
      `input[name="${this.currentRangeValues.maxSlider.name}"]`
    );

    if (minInput) {
      minInput.value = this.currentRangeValues.min;
    }
    if (maxInput) {
      maxInput.value = this.currentRangeValues.max;
    }
  }

  handleRangeHandleDrag(event) {
    event.preventDefault();
    event.stopPropagation();

    const handle = event.target;
    const isMinHandle = handle.classList.contains(
      "price-range-slider__handle--min"
    );
    const isMaxHandle = handle.classList.contains(
      "price-range-slider__handle--max"
    );

    if (!isMinHandle && !isMaxHandle) return;

    const container = handle.closest(".price-range-slider");
    if (!container) return;

    const track = container.querySelector(".price-range-slider__track");
    const minSlider = container.querySelector(
      ".price-range-slider__input--min"
    );
    const maxSlider = container.querySelector(
      ".price-range-slider__input--max"
    );

    if (!track || !minSlider || !maxSlider) return;

    const trackRect = track.getBoundingClientRect();
    const trackWidth = trackRect.width;
    const minValue = parseInt(minSlider.min);
    const maxValue = parseInt(minSlider.max);

    const handleDrag = (e) => {
      let clientX;
      if (e.type.includes("touch")) {
        if (e.touches && e.touches.length > 0) {
          clientX = e.touches[0].clientX;
        } else if (e.changedTouches && e.changedTouches.length > 0) {
          clientX = e.changedTouches[0].clientX;
        } else {
          return; // No valid touch data
        }
      } else {
        clientX = e.clientX;
      }

      const x = clientX - trackRect.left;
      const percentage = Math.max(0, Math.min(100, (x / trackWidth) * 100));
      const value = Math.round(
        minValue + (percentage / 100) * (maxValue - minValue)
      );

      if (isMinHandle) {
        const maxVal = parseInt(maxSlider.value);
        if (value < maxVal - 1) {
          minSlider.value = value;
          this.updateRangeSlider(container, value, maxVal);
        }
      } else if (isMaxHandle) {
        const minVal = parseInt(minSlider.value);
        if (value > minVal + 1) {
          maxSlider.value = value;
          this.updateRangeSlider(container, minVal, value);
        }
      }
    };

    const handleDragEnd = () => {
      document.removeEventListener("mousemove", handleDrag);
      document.removeEventListener("mouseup", handleDragEnd);
      document.removeEventListener("touchmove", handleDrag);
      document.removeEventListener("touchend", handleDragEnd);

      clearTimeout(this.rangeSliderTimeout);
      this.rangeSliderTimeout = setTimeout(() => {
        this.handlePriceSubmit(event);
      }, 100);
    };

    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDrag);
    document.addEventListener("touchend", handleDragEnd);
  }

  updateRangeSlider(container, minValue, maxValue) {
    const rangeElement = container.querySelector(".price-range-slider__range");
    const minHandle = container.querySelector(
      ".price-range-slider__handle--min"
    );
    const maxHandle = container.querySelector(
      ".price-range-slider__handle--max"
    );
    const minSlider = container.querySelector(
      ".price-range-slider__input--min"
    );
    const maxSlider = container.querySelector(
      ".price-range-slider__input--max"
    );

    if (!rangeElement || !minHandle || !maxHandle || !minSlider || !maxSlider)
      return;

    const minPercent = (minValue / parseInt(minSlider.max)) * 100;
    const maxPercent = (maxValue / parseInt(maxSlider.max)) * 100;

    // Update range bar
    rangeElement.style.left = minPercent + "%";
    rangeElement.style.width = maxPercent - minPercent + "%";

    // Update handle positions
    minHandle.style.left = minPercent + "%";
    maxHandle.style.left = maxPercent + "%";

    // Update value displays
    const minValueDisplay =
      container.querySelector('[id*="min-value"]') ||
      container.querySelector(
        ".price-range-slider__value-display:first-of-type"
      );
    const maxValueDisplay =
      container.querySelector('[id*="max-value"]') ||
      container.querySelector(
        ".price-range-slider__value-display:last-of-type"
      );

    if (minValueDisplay) {
      minValueDisplay.textContent = "$" + minValue.toLocaleString();
    }
    if (maxValueDisplay) {
      maxValueDisplay.textContent = "$" + maxValue.toLocaleString();
    }

    // Store current values only if we're not in the middle of restoration
    if (!this.isRestoringRangeValues) {
      this.currentRangeValues = {
        min: minValue,
        max: maxValue,
        minSlider: minSlider,
        maxSlider: maxSlider,
      };
    }
  }

  handleRangeSliderChange(event) {
    // Skip if we're currently restoring values to prevent infinite loops
    if (this.isRestoringRangeValues) return;

    const slider = event.target;
    const isMinSlider = slider.classList.contains(
      "price-range-slider__input--min"
    );
    const isMaxSlider = slider.classList.contains(
      "price-range-slider__input--max"
    );

    if (!isMinSlider && !isMaxSlider) return;

    // Get the container and other slider
    const container = slider.closest(".price-range-slider");
    if (!container) {
      return;
    }

    // Debug logging removed for performance

    const minSlider = container.querySelector(
      ".price-range-slider__input--min"
    );
    const maxSlider = container.querySelector(
      ".price-range-slider__input--max"
    );
    const rangeElement = container.querySelector(".price-range-slider__range");

    if (!minSlider || !maxSlider || !rangeElement) {
      return;
    }

    // Find the value displays by looking for the specific IDs within this container
    let minValueDisplay = container.querySelector('[id*="min-value"]');
    let maxValueDisplay = container.querySelector('[id*="max-value"]');

    // Fallback: if not found by ID, try by position
    if (!minValueDisplay || !maxValueDisplay) {
      const valueDisplays = container.querySelectorAll(
        ".price-range-slider__value-display"
      );
      if (valueDisplays.length >= 2) {
        minValueDisplay = valueDisplays[0];
        maxValueDisplay = valueDisplays[1];
      }
    }

    // Get current values
    let minValue = parseInt(minSlider.value);
    let maxValue = parseInt(maxSlider.value);

    // Apply constraints to prevent sliders from crossing
    if (isMinSlider) {
      // If min slider is being moved, ensure it doesn't exceed max
      if (minValue >= maxValue) {
        minValue = maxValue - 1;
        minSlider.value = minValue;
      }
    } else if (isMaxSlider) {
      // If max slider is being moved, ensure it doesn't go below min
      if (maxValue <= minValue) {
        maxValue = minValue + 1;
        maxSlider.value = maxValue;
      }
    }

    // Update the range display
    const minPercent = (minValue / parseInt(minSlider.max)) * 100;
    const maxPercent = (maxValue / parseInt(maxSlider.max)) * 100;

    rangeElement.style.left = minPercent + "%";
    rangeElement.style.width = maxPercent - minPercent + "%";

    // Update value displays
    if (minValueDisplay) {
      minValueDisplay.textContent = "$" + minValue.toLocaleString();
    }
    if (maxValueDisplay) {
      maxValueDisplay.textContent = "$" + maxValue.toLocaleString();
    }

    // Store current values to prevent reset after form submission
    this.currentRangeValues = {
      min: minValue,
      max: maxValue,
      minSlider: minSlider,
      maxSlider: maxSlider,
    };

    // Update form inputs immediately
    this.updateFormInputsWithRangeValues();

    // Submit immediately on all devices - no difference between mobile and desktop
    this.handlePriceSubmit(event);
  }

  handleSortChange(event) {
    const form = document.querySelector("#FacetFiltersForm");
    if (!form) {
      return;
    }

    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();

    this.renderPage(searchParams, event);
  }

  handleRemoveFilter(event) {
    event.preventDefault();

    const link = event.target.closest("a");
    if (!link) {
      return;
    }

    const href = link.href;

    // Check if this is the "Clear All" button
    if (link.classList.contains("selected-filters__clear-all")) {
      // Clear the form first to ensure no cached state
      this.clearFormState();

      // Clear cache to ensure fresh data
      this.filterData = [];

      // Clear all filters by going to base collection URL with no parameters
      this.renderPage("", event);
      return;
    }

    try {
      const url = new URL(href);
      const searchParams = url.search.slice(1);

      this.renderPage(searchParams, event);
    } catch (error) {
      window.location.href = href;
    }
  }

  renderPage(searchParams, event, updateURLHash = true) {
    this.searchParamsPrev = searchParams;

    // Add loading states with better UX
    this.addLoadingStates();

    // Build URL
    const url = `${window.location.pathname}?${searchParams}`;

    // Check cache first
    const cachedData = this.filterData.find((item) => item.url === url);
    if (cachedData) {
      this.renderContent(cachedData.html, event);
    } else {
      this.fetchAndRender(url, event);
    }

    if (updateURLHash) {
      this.updateURLHash(searchParams);
    }
  }

  fetchAndRender(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.text();
      })
      .then((html) => {
        // Cache the result
        this.filterData.push({ html, url });

        // Render the content
        this.renderContent(html, event);
      })
      .catch((error) => {
        // Fallback to page reload
        window.location.href = url;
      });
  }

  renderContent(html, event) {
    const parser = new DOMParser();
    const newDoc = parser.parseFromString(html, "text/html");

    // Update product grid
    this.updateProductGrid(newDoc);

    // Update sidebar filters
    this.updateSidebar(newDoc);

    // Update product count
    this.updateProductCount(newDoc);

    // Update pagination
    this.updatePagination(newDoc);

    // Update header section (product count and filter button)
    this.updateHeaderSection(newDoc);

    // Force mobile drawer content refresh
    this.refreshMobileDrawerContent(newDoc);

    // Ensure mobile drawer state is synchronized
    this.syncMobileDrawerState();

    // Handle mobile drawer state if needed
    if (this.keepMobileDrawerOpen) {
      // Reset the flag
      this.keepMobileDrawerOpen = false;
    }

    // Remove loading states
    this.removeLoadingStates();
  }

  updateProductGrid(newDoc) {
    const currentGrid = document.getElementById("ProductGridContainer");
    const newGrid = newDoc.getElementById("ProductGridContainer");

    if (currentGrid && newGrid) {
      currentGrid.innerHTML = newGrid.innerHTML;
    }
  }

  updateSidebar(newDoc) {
    // Store current range values before updating DOM
    const storedRangeValues = this.currentRangeValues
      ? { ...this.currentRangeValues }
      : null;

    // Update desktop sidebar
    const currentSidebar = document.querySelector(".collection-sidebar");
    const newSidebar = newDoc.querySelector(".collection-sidebar");

    if (currentSidebar && newSidebar) {
      // Store current open states
      const openStates = this.getFilterOpenStates(currentSidebar);

      // Update content
      currentSidebar.innerHTML = newSidebar.innerHTML;

      // Restore open states immediately
      this.restoreFilterOpenStates(currentSidebar, openStates);

      // Restore range slider values BEFORE initializing if they were stored
      if (storedRangeValues) {
        this.currentRangeValues = storedRangeValues;
      }

      // Reinitialize range sliders in desktop sidebar
      this.initializeRangeSliders();

      // Restore range slider values after initialization
      if (storedRangeValues) {
        this.restoreRangeSliderValues();
      }
    }

    // Update mobile drawer
    const currentDrawer = document.querySelector(".drawer__scrollable");
    const newDrawer = newDoc.querySelector(".drawer__scrollable");

    if (currentDrawer && newDrawer) {
      // Store current open states for mobile drawer
      const drawerOpenStates = this.getFilterOpenStates(currentDrawer);

      // Update content
      currentDrawer.innerHTML = newDrawer.innerHTML;

      // Restore open states immediately
      this.restoreFilterOpenStates(currentDrawer, drawerOpenStates);

      // Restore range slider values BEFORE initializing if they were stored
      if (storedRangeValues) {
        this.currentRangeValues = storedRangeValues;
      }

      // Reinitialize range sliders in mobile drawer
      this.initializeRangeSliders();

      // Restore range slider values after initialization
      if (storedRangeValues) {
        this.restoreRangeSliderValues();
      }
    }
  }

  getFilterOpenStates(sidebar) {
    const openStates = {};
    const groups = sidebar.querySelectorAll(".collection-sidebar__group");

    groups.forEach((group) => {
      const content = group.querySelector(".collapsible-content--sidebar");
      if (content) {
        const match = group.className.match(
          /collection-sidebar__group--([^\s]+)/
        );
        if (match) {
          openStates[match[1]] = content.classList.contains("is-open");
        }
      }
    });

    return openStates;
  }

  restoreFilterOpenStates(sidebar, openStates) {
    const groups = sidebar.querySelectorAll(".collection-sidebar__group");

    groups.forEach((group) => {
      const content = group.querySelector(".collapsible-content--sidebar");
      if (content) {
        const match = group.className.match(
          /collection-sidebar__group--([^\s]+)/
        );
        if (match && openStates[match[1]]) {
          content.classList.add("is-open");
        }
      }
    });
  }

  updateProductCount(newDoc) {
    // This method is now handled by updateHeaderSection
    // Keeping it for backward compatibility but the actual updates
    // are done in updateHeaderSection method
  }

  updatePagination(newDoc) {
    const currentPagination = document.querySelector(".pagination");
    const newPagination = newDoc.querySelector(".pagination");

    if (currentPagination && newPagination) {
      currentPagination.innerHTML = newPagination.innerHTML;
    } else if (!currentPagination && newPagination) {
      // If pagination doesn't exist but should be added
      const collectionSection = document.getElementById("CollectionSection");
      if (collectionSection) {
        collectionSection.appendChild(newPagination);
      }
    } else if (currentPagination && !newPagination) {
      // If pagination should be removed
      currentPagination.remove();
    }
  }

  updateHeaderSection(newDoc) {
    // Update only the product count, not the entire filter section
    // This preserves the mobile filter button's functionality
    const currentCount = document.querySelector(
      ".collection-filter__item--count"
    );
    const newCount = newDoc.querySelector(".collection-filter__item--count");

    if (currentCount && newCount) {
      currentCount.innerHTML = newCount.innerHTML;
    }

    // Update mobile product count paragraph
    const currentMobileCount = document.querySelector(
      ".medium-up--hide.text-center[data-scroll-to]"
    );
    const newMobileCount = newDoc.querySelector(
      ".medium-up--hide.text-center[data-scroll-to]"
    );

    if (currentMobileCount && newMobileCount) {
      currentMobileCount.innerHTML = newMobileCount.innerHTML;
    }
  }

  refreshMobileDrawerContent(newDoc) {
    // Get the new mobile drawer content from the fetched HTML
    const newMobileDrawer = newDoc.querySelector(
      "#FilterDrawer .drawer__scrollable"
    );
    const currentMobileDrawer = document.querySelector(
      "#FilterDrawer .drawer__scrollable"
    );

    if (newMobileDrawer && currentMobileDrawer) {
      // Store current open states before updating
      const currentOpenStates = this.getFilterOpenStates(currentMobileDrawer);

      // Update the mobile drawer content
      currentMobileDrawer.innerHTML = newMobileDrawer.innerHTML;

      // Restore open states immediately
      this.restoreFilterOpenStates(currentMobileDrawer, currentOpenStates);

      // Restore range slider values after content refresh
      if (this.currentRangeValues) {
        this.restoreRangeSliderValues();
      }

      // Apply pending filter state if it exists
      if (this.pendingFilterState) {
        console.log(
          "Applying pending filter state in refreshMobileDrawerContent:",
          this.pendingFilterState
        );
        const mobileCheckboxes =
          currentMobileDrawer.querySelectorAll(".facets__checkbox");
        mobileCheckboxes.forEach((checkbox) => {
          if (
            checkbox.name === this.pendingFilterState.name &&
            checkbox.value === this.pendingFilterState.value
          ) {
            checkbox.checked = this.pendingFilterState.checked;
            console.log(
              "Applied pending state to checkbox in refreshMobileDrawerContent:",
              checkbox
            );
          }
        });
      }
    }
  }

  updateMobileDrawerFromForm(form) {
    console.log("Updating mobile drawer from form state...");

    const mobileDrawer = document.querySelector(
      "#FilterDrawer .drawer__scrollable"
    );
    if (!mobileDrawer) return;

    // Get all form checkboxes
    const formCheckboxes = form.querySelectorAll(".facets__checkbox");
    const mobileCheckboxes = mobileDrawer.querySelectorAll(".facets__checkbox");

    // Create a map of form checkbox states
    const formCheckboxStates = {};
    formCheckboxes.forEach((checkbox) => {
      const key = `${checkbox.name}-${checkbox.value}`;
      formCheckboxStates[key] = checkbox.checked;
    });

    // Apply form states to mobile checkboxes
    mobileCheckboxes.forEach((checkbox) => {
      const key = `${checkbox.name}-${checkbox.value}`;
      if (formCheckboxStates.hasOwnProperty(key)) {
        checkbox.checked = formCheckboxStates[key];
      } else {
        // If form doesn't have this checkbox, uncheck it in mobile
        checkbox.checked = false;
      }
    });

    // Apply pending filter state if it exists
    if (this.pendingFilterState) {
      console.log(
        "Applying pending filter state in updateMobileDrawerFromForm:",
        this.pendingFilterState
      );
      mobileCheckboxes.forEach((checkbox) => {
        if (
          checkbox.name === this.pendingFilterState.name &&
          checkbox.value === this.pendingFilterState.value
        ) {
          checkbox.checked = this.pendingFilterState.checked;
          console.log(
            "Applied pending state to checkbox in updateMobileDrawerFromForm:",
            checkbox
          );
        }
      });
    }

    // Update price inputs
    const formPriceInputs = form.querySelectorAll(".facets__price-input");
    const mobilePriceInputs = mobileDrawer.querySelectorAll(
      ".facets__price-input"
    );

    const formPriceStates = {};
    formPriceInputs.forEach((input) => {
      formPriceStates[input.name] = input.value;
    });

    mobilePriceInputs.forEach((input) => {
      if (formPriceStates.hasOwnProperty(input.name)) {
        input.value = formPriceStates[input.name];
      } else {
        // If form doesn't have this input, clear it in mobile
        input.value = "";
      }
    });

    console.log("Mobile drawer updated from form state");
  }

  initializeMobileDrawerState() {
    console.log("Initializing mobile drawer state...");

    const form = document.querySelector("#FacetFiltersForm");
    if (form) {
      this.updateMobileDrawerFromForm(form);
      console.log("Mobile drawer state initialized");
    }
  }

  initializeRangeSliders() {
    console.log("Initializing range sliders...");

    const rangeSliders = document.querySelectorAll(".price-range-slider");
    rangeSliders.forEach((slider) => {
      this.setupRangeSlider(slider);
    });

    console.log("Range sliders initialized");
  }

  initializeSliderValues(minSlider, maxSlider) {
    // Don't initialize if we're in the middle of restoring values
    if (this.isRestoringRangeValues) {
      console.log("Skipping initializeSliderValues - restoration in progress");
      return;
    }

    // Get current URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const minParam = urlParams.get(minSlider.name);
    const maxParam = urlParams.get(maxSlider.name);

    console.log("initializeSliderValues called:", {
      minParam: minParam,
      maxParam: maxParam,
      currentRangeValues: this.currentRangeValues,
      sliderName: minSlider.name,
    });

    // Prioritize stored values over URL parameters if we have fresh user input
    if (
      this.currentRangeValues &&
      this.currentRangeValues.minSlider &&
      this.currentRangeValues.maxSlider
    ) {
      // If we have stored values with DOM references, use them (fresh user input)
      if (
        this.currentRangeValues.min >= parseInt(minSlider.min) &&
        this.currentRangeValues.min <= parseInt(minSlider.max)
      ) {
        minSlider.value = this.currentRangeValues.min;
      }
      if (
        this.currentRangeValues.max >= parseInt(maxSlider.min) &&
        this.currentRangeValues.max <= parseInt(maxSlider.max)
      ) {
        maxSlider.value = this.currentRangeValues.max;
      }
    } else if (minParam && maxParam) {
      // If no stored values, use URL parameters
      const minValue = parseInt(minParam);
      const maxValue = parseInt(maxParam);

      // Ensure values are within slider bounds
      if (
        minValue >= parseInt(minSlider.min) &&
        minValue <= parseInt(minSlider.max)
      ) {
        minSlider.value = minValue;
      }
      if (
        maxValue >= parseInt(maxSlider.min) &&
        maxValue <= parseInt(maxSlider.max)
      ) {
        maxSlider.value = maxValue;
      }
    } else if (this.currentRangeValues) {
      // If we have stored values, use them
      if (
        this.currentRangeValues.min >= parseInt(minSlider.min) &&
        this.currentRangeValues.min <= parseInt(minSlider.max)
      ) {
        minSlider.value = this.currentRangeValues.min;
      }
      if (
        this.currentRangeValues.max >= parseInt(maxSlider.min) &&
        this.currentRangeValues.max <= parseInt(maxSlider.max)
      ) {
        maxSlider.value = this.currentRangeValues.max;
      }
    } else {
      // If no URL parameters and no stored values, check if sliders have default values
      // If they're at 0, set them to reasonable defaults
      if (parseInt(minSlider.value) === 0 && parseInt(maxSlider.value) === 0) {
        minSlider.value = minSlider.min;
        maxSlider.value = maxSlider.max;
      }
    }
  }

  setupRangeSlider(container) {
    const minSlider = container.querySelector(
      ".price-range-slider__input--min"
    );
    const maxSlider = container.querySelector(
      ".price-range-slider__input--max"
    );
    const rangeElement = container.querySelector(".price-range-slider__range");
    const minHandle = container.querySelector(
      ".price-range-slider__handle--min"
    );
    const maxHandle = container.querySelector(
      ".price-range-slider__handle--max"
    );

    // Find the value displays by looking for the specific IDs within this container
    let minValueDisplay = container.querySelector('[id*="min-value"]');
    let maxValueDisplay = container.querySelector('[id*="max-value"]');

    // Fallback: if not found by ID, try by position
    if (!minValueDisplay || !maxValueDisplay) {
      const valueDisplays = container.querySelectorAll(
        ".price-range-slider__value-display"
      );
      if (valueDisplays.length >= 2) {
        minValueDisplay = valueDisplays[0];
        maxValueDisplay = valueDisplays[1];
      }
    }

    if (!minSlider || !maxSlider || !rangeElement || !minHandle || !maxHandle) {
      return;
    }

    // Initialize values from URL parameters or form data if not already set
    this.initializeSliderValues(minSlider, maxSlider);

    // Set initial range display
    const minValue = parseInt(minSlider.value);
    const maxValue = parseInt(maxSlider.value);

    // Only store current values if we don't already have stored values
    // This prevents overriding restored values
    if (!this.currentRangeValues) {
      this.currentRangeValues = {
        min: minValue,
        max: maxValue,
        minSlider: minSlider,
        maxSlider: maxSlider,
      };
    } else {
      // Update the DOM element references in existing stored values
      this.currentRangeValues.minSlider = minSlider;
      this.currentRangeValues.maxSlider = maxSlider;
    }

    // Use the new updateRangeSlider method
    this.updateRangeSlider(container, minValue, maxValue);
  }

  restoreRangeSliderValues() {
    if (!this.currentRangeValues || this.isRestoringRangeValues) {
      return;
    }

    // Prevent recursive calls
    this.isRestoringRangeValues = true;

    // Find all range sliders and restore their values
    const rangeSliders = document.querySelectorAll(".price-range-slider");
    rangeSliders.forEach((container) => {
      const minSlider = container.querySelector(
        ".price-range-slider__input--min"
      );
      const maxSlider = container.querySelector(
        ".price-range-slider__input--max"
      );
      const rangeElement = container.querySelector(
        ".price-range-slider__range"
      );

      if (minSlider && maxSlider && rangeElement) {
        minSlider.value = this.currentRangeValues.min;
        maxSlider.value = this.currentRangeValues.max;

        // Update the visual range
        const minPercent =
          (this.currentRangeValues.min / parseInt(minSlider.max)) * 100;
        const maxPercent =
          (this.currentRangeValues.max / parseInt(maxSlider.max)) * 100;

        rangeElement.style.left = minPercent + "%";
        rangeElement.style.width = maxPercent - minPercent + "%";

        // Update handle positions
        const minHandle = container.querySelector(
          ".price-range-slider__handle--min"
        );
        const maxHandle = container.querySelector(
          ".price-range-slider__handle--max"
        );

        if (minHandle && maxHandle) {
          minHandle.style.left = minPercent + "%";
          maxHandle.style.left = maxPercent + "%";
        }

        // Update value displays
        const minValueDisplay =
          container.querySelector('[id*="min-value"]') ||
          container.querySelector(
            ".price-range-slider__value-display:first-of-type"
          );
        const maxValueDisplay =
          container.querySelector('[id*="max-value"]') ||
          container.querySelector(
            ".price-range-slider__value-display:last-of-type"
          );

        console.log("Updating value displays:", {
          minValueDisplay: minValueDisplay,
          maxValueDisplay: maxValueDisplay,
          minValue: this.currentRangeValues.min,
          maxValue: this.currentRangeValues.max,
        });

        if (minValueDisplay) {
          minValueDisplay.textContent =
            "$" + this.currentRangeValues.min.toLocaleString();
          console.log("Updated min display to:", minValueDisplay.textContent);
        }
        if (maxValueDisplay) {
          maxValueDisplay.textContent =
            "$" + this.currentRangeValues.max.toLocaleString();
          console.log("Updated max display to:", maxValueDisplay.textContent);
        }

        // Update the stored values with the new DOM elements
        this.currentRangeValues.minSlider = minSlider;
        this.currentRangeValues.maxSlider = maxSlider;

        // Update form inputs without triggering events
        this.updateFormInputsWithRangeValues();
      }
    });

    // Reset the flag immediately
    this.isRestoringRangeValues = false;
  }

  preloadCommonFilters() {
    console.log("Preloading common filter combinations...");

    const form = document.querySelector("#FacetFiltersForm");
    if (!form) return;

    // Get all available filters
    const checkboxes = form.querySelectorAll(".facets__checkbox");
    const commonFilters = [];

    // Preload first few filter options (most commonly used)
    checkboxes.forEach((checkbox, index) => {
      if (index < 3 && !checkbox.checked) {
        // Preload first 3 unchecked filters
        commonFilters.push({
          name: checkbox.name,
          value: checkbox.value,
        });
      }
    });

    // Preload these combinations immediately
    commonFilters.forEach((filter) => {
      const url = `${window.location.pathname}?${
        filter.name
      }=${encodeURIComponent(filter.value)}`;
      this.preloadFilter(url);
    });
  }

  preloadFilter(url) {
    // Only preload if not already cached
    const cachedData = this.filterData.find((item) => item.url === url);
    if (cachedData) return;

    console.log("Preloading filter:", url);

    fetch(url)
      .then((response) => {
        if (response.ok) {
          return response.text();
        }
        throw new Error(`HTTP ${response.status}`);
      })
      .then((html) => {
        // Cache the result silently
        this.filterData.push({ html, url });
        console.log("Preloaded filter cached:", url);
      })
      .catch((error) => {
        console.log("Preload failed (this is normal):", error.message);
      });
  }

  storeCurrentFilterState(checkbox) {
    console.log("Storing current filter state...");

    // Store the checkbox state that the user just changed
    this.pendingFilterState = {
      name: checkbox.name,
      value: checkbox.value,
      checked: checkbox.checked,
    };
  }

  addOptimisticUpdate(checkbox) {
    console.log("Adding optimistic update...");

    // Add visual feedback immediately
    const filterItem = checkbox.closest(".tag");
    if (filterItem) {
      filterItem.classList.add("tag--optimistic");
    }
  }

  addLoadingStates() {
    console.log("Adding loading states...");

    const productGrid = document.getElementById("ProductGrid");
    const countContainer = document.querySelector(
      ".collection-filter__item--count"
    );

    if (productGrid) {
      productGrid.classList.add("loading");
      console.log("Added loading state to product grid");
    }
    if (countContainer) {
      countContainer.classList.add("loading");
      console.log("Added loading state to count container");
    }

    // Add loading state to filter button
    const filterButton = document.querySelector(
      ".js-drawer-open-collection-filters"
    );
    if (filterButton) {
      filterButton.classList.add("loading");
    }
  }

  removeLoadingStates() {
    console.log("Removing loading states...");

    const productGrid = document.getElementById("ProductGrid");
    const countContainer = document.querySelector(
      ".collection-filter__item--count"
    );
    const filterButton = document.querySelector(
      ".js-drawer-open-collection-filters"
    );

    if (productGrid) {
      productGrid.classList.remove("loading");
    }
    if (countContainer) {
      countContainer.classList.remove("loading");
    }
    if (filterButton) {
      filterButton.classList.remove("loading");
    }
  }

  updateURLHash(searchParams) {
    const url = `${window.location.pathname}${
      searchParams ? "?" + searchParams : ""
    }`;
    window.history.pushState({ searchParams }, "", url);
  }

  syncMobileDrawerState() {
    console.log("Synchronizing mobile drawer state...");

    // Ensure mobile drawer filters are in sync with desktop sidebar
    const desktopSidebar = document.querySelector(".collection-sidebar");
    const mobileDrawer = document.querySelector(
      "#FilterDrawer .drawer__scrollable"
    );

    if (desktopSidebar && mobileDrawer) {
      // Sync checkbox states by matching name and value
      const desktopCheckboxes =
        desktopSidebar.querySelectorAll(".facets__checkbox");
      const mobileCheckboxes =
        mobileDrawer.querySelectorAll(".facets__checkbox");

      // Create a map of desktop checkbox states
      const desktopCheckboxStates = {};
      desktopCheckboxes.forEach((checkbox) => {
        const key = `${checkbox.name}-${checkbox.value}`;
        desktopCheckboxStates[key] = checkbox.checked;
      });

      // Apply desktop states to mobile checkboxes
      mobileCheckboxes.forEach((checkbox) => {
        const key = `${checkbox.name}-${checkbox.value}`;
        if (desktopCheckboxStates.hasOwnProperty(key)) {
          checkbox.checked = desktopCheckboxStates[key];
        } else {
          // If desktop doesn't have this checkbox, uncheck it in mobile
          checkbox.checked = false;
        }
      });

      // Apply pending filter state if it exists
      if (this.pendingFilterState) {
        console.log("Applying pending filter state:", this.pendingFilterState);
        mobileCheckboxes.forEach((checkbox) => {
          if (
            checkbox.name === this.pendingFilterState.name &&
            checkbox.value === this.pendingFilterState.value
          ) {
            checkbox.checked = this.pendingFilterState.checked;
            console.log("Applied pending state to checkbox:", checkbox);
          }
        });
        // Clear the pending state
        this.pendingFilterState = null;
      }

      // Sync price input values by matching name
      const desktopPriceInputs = desktopSidebar.querySelectorAll(
        ".facets__price-input"
      );
      const mobilePriceInputs = mobileDrawer.querySelectorAll(
        ".facets__price-input"
      );

      // Create a map of desktop price input values
      const desktopPriceStates = {};
      desktopPriceInputs.forEach((input) => {
        desktopPriceStates[input.name] = input.value;
      });

      // Apply desktop values to mobile price inputs
      mobilePriceInputs.forEach((input) => {
        if (desktopPriceStates.hasOwnProperty(input.name)) {
          input.value = desktopPriceStates[input.name];
        } else {
          input.value = "";
        }
      });

      console.log("Mobile drawer state synchronized");
    }
  }

  forceMobileDrawerSync() {
    const form = document.querySelector("#FacetFiltersForm");
    const mobileDrawer = document.querySelector(
      "#FilterDrawer .drawer__scrollable"
    );

    if (!form || !mobileDrawer) {
      console.log("Form or mobile drawer not found, skipping sync");
      return;
    }

    try {
      // Get current form state
      const formCheckboxes = form.querySelectorAll(".facets__checkbox");
      const mobileCheckboxes =
        mobileDrawer.querySelectorAll(".facets__checkbox");

      // Create a comprehensive map of form checkbox states
      const formCheckboxStates = {};
      formCheckboxes.forEach((checkbox) => {
        const key = `${checkbox.name}-${checkbox.value}`;
        formCheckboxStates[key] = checkbox.checked;
      });

      // Force sync all mobile checkboxes with form state
      mobileCheckboxes.forEach((checkbox) => {
        const key = `${checkbox.name}-${checkbox.value}`;
        if (formCheckboxStates.hasOwnProperty(key)) {
          checkbox.checked = formCheckboxStates[key];
        } else {
          // If form doesn't have this checkbox, uncheck it
          checkbox.checked = false;
        }
      });

      // Sync price inputs
      const formPriceInputs = form.querySelectorAll(".facets__price-input");
      const mobilePriceInputs = mobileDrawer.querySelectorAll(
        ".facets__price-input"
      );

      const formPriceStates = {};
      formPriceInputs.forEach((input) => {
        formPriceStates[input.name] = input.value;
      });

      mobilePriceInputs.forEach((input) => {
        if (formPriceStates.hasOwnProperty(input.name)) {
          input.value = formPriceStates[input.name];
        } else {
          input.value = "";
        }
      });

      console.log("Mobile drawer force sync completed successfully");
    } catch (error) {
      console.error("Error during mobile drawer sync:", error);
    }
  }

  clearFormState() {
    console.log("Clearing form state...");

    const form = document.querySelector("#FacetFiltersForm");
    if (form) {
      // Clear all checkboxes
      const checkboxes = form.querySelectorAll(".facets__checkbox");
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      // Clear all price inputs
      const priceInputs = form.querySelectorAll(".facets__price-input");
      priceInputs.forEach((input) => {
        input.value = "";
      });

      console.log("Form state cleared");
    }
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupDrawerObserver() {
    // Watch the body element for drawer state changes
    // The theme likely adds classes to body when drawers open
    const body = document.body;
    if (!body) {
      console.log("Body element not found, skipping observer setup");
      return;
    }

    this.drawerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === "attributes" &&
          mutation.attributeName === "class"
        ) {
          const target = mutation.target;
          console.log(
            "Class change detected on:",
            target,
            "Classes:",
            target.className
          );

          // Check if drawer is opening (has js-drawer-open class)
          if (target.classList.contains("js-drawer-open")) {
            console.log("Drawer opened - syncing state");
            this.forceMobileDrawerSync();
          }
        }
      });
    });

    this.drawerObserver.observe(body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    console.log("Drawer observer set up successfully");
  }

  cleanup() {
    console.log("Cleaning up FacetFiltersForm...");

    // Remove event listeners
    if (this.filterChangeHandler) {
      document.removeEventListener("change", this.filterChangeHandler);
    }
    if (this.priceSubmitHandler) {
      document.removeEventListener("click", this.priceSubmitHandler);
    }
    if (this.rangeSliderHandler) {
      document.removeEventListener("input", this.rangeSliderHandler);
      document.removeEventListener("change", this.rangeSliderHandler);
    }
    if (this.mobileRangeHandler) {
      document.removeEventListener("touchstart", this.mobileRangeHandler);
      document.removeEventListener("touchmove", this.mobileRangeHandler);
      document.removeEventListener("touchend", this.mobileRangeHandler);
    }
    if (this.rangeHandleHandler) {
      document.removeEventListener("mousedown", this.rangeHandleHandler);
      document.removeEventListener("touchstart", this.rangeHandleHandler);
    }
    if (this.sortChangeHandler) {
      document.removeEventListener("change", this.sortChangeHandler);
    }
    if (this.removeFilterHandler) {
      document.removeEventListener("click", this.removeFilterHandler);
    }

    // Clear any pending timeouts
    if (this.rangeSliderTimeout) {
      clearTimeout(this.rangeSliderTimeout);
    }

    // Disconnect observer
    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
    }

    this.eventListenersSetup = false;
    console.log("FacetFiltersForm cleaned up");
  }
}

// Initialize when DOM is ready - with proper theme integration
if (window.facetFilters) {
  console.log("Cleaning up existing FacetFiltersForm instance...");
  window.facetFilters.cleanup();
}

// Initialize facets with theme integration
const initializeFacets = () => {
  window.facetFilters = new FacetFiltersForm();
};

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initializeFacets);
} else {
  initializeFacets();
}
